import { useState, useContext, useEffect } from 'react';
import { MenuItem, FormControl, InputLabel } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import {
  WorkflowEngineContext,
  WorkflowEngineActionKind
} from '../WorkflowEngine/workflowEngineReducer';
import getPagePermissionForRole from '../Shared/jsonSchemaUtilities/getPagePermissionForRole';
import getPageRoles from '../Shared/jsonSchemaUtilities/getPageRoles';
import { JSONSchema7 } from 'json-schema';
import JSONSchema7Properties from '../Shared/jSONSchema7Properties';

interface RolesPermission {
  [id: string]: string[];
}

const RoleSelector = () => {
  const { state, dispatch } = useContext(WorkflowEngineContext);

  const { pages, currentPage, isReadOnlyView, role } = state;

  const [formRoles, setFormRoles] = useState<string[]>([]);
  const [rolesAccess, setRolesAccess] = useState<RolesPermission>({});
  const [impersonatedRole, setImpersonatedRole] = useState<string>('');
  const [originalRole, setOriginalRole] = useState<string>('');

  const handleRoleSelector = (event: SelectChangeEvent) => {
    if (event.target.value.length === 0) {
      restoreState();
      return;
    }

    isRoleBlocked(event.target.value);

    setImpersonatedRole(event.target.value as string);

    firstAvailablePage(event.target.value as string);
  };

  const isRoleBlocked = (role: string) => {
    const isBlocked =
      rolesAccess[role].reduce(
        (total, x) => (x === 'NoAccess' ? total + 1 : total),
        0
      ) === pages.length;

    dispatch({
      type: WorkflowEngineActionKind.SET_ACCESS,
      payload: !isBlocked
    });
  };

  const firstAvailablePage = (role: string) => {
    const pageIndex = rolesAccess[role].findIndex(
      (level) => level != 'NoAccess'
    );
    if (pageIndex === -1) return;

    dispatch({
      type: WorkflowEngineActionKind.SET_CURRENT_PAGE,
      payload: pageIndex
    });

    setPermissionRole(role);
  };

  const setPermissionRole = (role: string) => {
    dispatch({
      type: WorkflowEngineActionKind.SET_ROLE,
      payload: role
    });
  };

  const restoreState = () => {
    setPermissionRole(originalRole);
    setImpersonatedRole('');
    dispatch({
      type: WorkflowEngineActionKind.RESTORE_PREVIEW_ACCESS
    });
  };

  const getRolesInPage = (): RolesPermission => {
    const formPermissions: RolesPermission = {};

    formRoles.forEach((role: string) => {
      const permissions: string[] = [];
      pages.forEach((page) => {
        const pageSchema = (page.schema.properties as JSONSchema7Properties)[
          'page'
        ] as JSONSchema7;
        permissions.push(getPagePermissionForRole(pageSchema, role));
      });
      formPermissions[role] = permissions;
    });
    return formPermissions;
  };

  useEffect(() => {
    setFormRoles(getPageRoles(pages[0]));
    setOriginalRole(role);
  }, []);

  useEffect(() => {
    setRolesAccess(getRolesInPage());
  }, [formRoles]);

  useEffect(() => {
    setPermissionRole(impersonatedRole);
  }, [currentPage, isReadOnlyView]);

  return (
    <>
      <div
        style={{
          marginRight: '20px',
          width: '120px',
          maxWidth: '120px'
        }}
      >
        <FormControl fullWidth size="small">
          <InputLabel id="role-emulation-selector-label">
            View as ...
          </InputLabel>
          <Select
            labelId="role-emulation-selector-label"
            id="role-emulation-selector"
            value={impersonatedRole}
            label="View as ..."
            onChange={handleRoleSelector}
          >
            <MenuItem value={''}>
              <em>Select a role...</em>
            </MenuItem>
            {formRoles.map((role, index) => {
              return (
                <MenuItem key={`${role}-${index}`} value={role}>
                  {role}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </div>
    </>
  );
};

export default RoleSelector;
